import { isValidElement, ReactNode } from "react";
import { ProductResponse } from "../../../../types/product.type";
import { numberWithCommas } from "../../../../utils";
import { formatDateTimeBkk } from "../../../../utils/date";
import StatusPill from "../../shared/StatusPill";
import { formatCataloguePrice } from "../../shared/constants";
import { resolveProductUnit } from "../productUnit";
import {
  EXPORTED_LABEL,
  NOT_EXPORTED_LABEL,
  WEB_IT_EXPORT_LABEL,
} from "../webItExport";
import ImageInfo from "./ImageInfo";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { COMPACT_QUERY } from "../../../../utils/hooks/useCompact";

const compact = "@media " + COMPACT_QUERY;

const EMPTY_PLACEHOLDER = "-";

/** Whether the product's quantities count toward ยอดของเสีย. */
const WASTE_COUNT_LABEL = {
  excluded: "ไม่นับ",
  counted: "นับ",
} as const;

/** The subtitle under the name: "code · แบรนด์ · หมวด", skipping whichever the product lacks. */
const buildSubtitle = (
  productDetails: ProductResponse,
  categoryName: string,
): string =>
  [productDetails.code, productDetails.brand, categoryName]
    .filter(Boolean)
    .join(" · ");

interface ProductInfoProps {
  productDetails: ProductResponse;
  categoryName: string;
  canEdit: boolean;
  onEdit: () => void;
}

interface DetailsProps {
  label: string;
  value: string | number | ReactNode;
}

const GrayText = ({ children }: { children: ReactNode }) => (
  <Typography
    variant="caption"
    sx={{ color: (theme) => theme.palette.gray.main }}
  >
    {children}
  </Typography>
);

const Details = ({ label, value }: DetailsProps) => (
  <>
    <Grid size={5}>
      <Typography variant="caption" fontWeight={600}>
        {label}
      </Typography>
    </Grid>
    <Grid size={7}>
      {isValidElement(value) ? value : <GrayText>{value}</GrayText>}
    </Grid>
  </>
);

/** A yes/no attribute: a green tick or a red cross in front of its label. */
const FlagInfo = ({ isOn, label }: { isOn: boolean; label: string }) => (
  <Stack direction="row" alignItems="center">
    {isOn ? (
      <CheckCircleIcon color="success" sx={{ width: 16, mr: 0.5 }} />
    ) : (
      <CancelIcon color="error" sx={{ width: 16, mr: 0.5 }} />
    )}
    <GrayText>{label}</GrayText>
  </Stack>
);

const TaxInfo = ({ taxRate }: { taxRate: number }) => {
  const isTaxed = taxRate > 0;
  const taxLabel = isTaxed
    ? `คิดภาษี ${numberWithCommas(taxRate)}%`
    : "ไม่คิดภาษี";

  return <FlagInfo isOn={isTaxed} label={taxLabel} />;
};

const ExportInfo = ({ allowExport }: { allowExport: boolean }) => {
  const exportLabel = allowExport ? EXPORTED_LABEL : NOT_EXPORTED_LABEL;

  return <FlagInfo isOn={allowExport} label={exportLabel} />;
};

const BarcodeDetail = ({
  label,
  code,
}: {
  label: string;
  code?: string | null;
}) => (
  <Details
    label={label}
    value={
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {code && (
          <Box
            component="img"
            src="/icons/barcode.png"
            sx={{ height: 16, width: 16, mr: 0.5 }}
          />
        )}
        <GrayText>{code ?? EMPTY_PLACEHOLDER}</GrayText>
      </Box>
    }
  />
);

const BlockTitle = ({ children }: { children: ReactNode }) => (
  <Typography variant="caption" color="primary" fontWeight={700}>
    {children}
  </Typography>
);

/** The one card the REF puts every product attribute and both images in. */
export default function ProductInfo({
  productDetails,
  categoryName,
  canEdit,
  onEdit,
}: ProductInfoProps) {
  const shelfLife =
    productDetails.shelf_life_days === null
      ? EMPTY_PLACEHOLDER
      : `${productDetails.shelf_life_days} วัน`;
  const unit =
    resolveProductUnit(productDetails.units_json) ?? EMPTY_PLACEHOLDER;
  const facePrice = formatCataloguePrice(productDetails.face_price);
  const wasteCount = productDetails.exclude_from_waste
    ? WASTE_COUNT_LABEL.excluded
    : WASTE_COUNT_LABEL.counted;

  return (
    <Paper sx={{ padding: "16px 24px", [compact]: { padding: "12px 16px" } }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        gap={2}
        sx={{ mb: 2, [compact]: { mb: 1.25 } }}
      >
        <Box>
          <Typography
            variant="caption"
            sx={{ display: "block", color: (theme) => theme.palette.gray.main }}
          >
            {buildSubtitle(productDetails, categoryName)}
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            {productDetails.name}
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" gap={2}>
          <StatusPill status={productDetails.status} />
          {canEdit && (
            <Button
              variant="contained"
              startIcon={<EditOutlinedIcon />}
              onClick={onEdit}
            >
              แก้ไขสินค้า
            </Button>
          )}
        </Stack>
      </Stack>

      <Grid container spacing={{ xs: 2, lg: 3 }}>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <BlockTitle>ข้อมูลทั่วไป</BlockTitle>
          <Grid container>
            <Details label="รหัสสินค้า" value={productDetails.code} />
            <Details label="ชื่อสินค้า" value={productDetails.name} />
            <Details
              label="รายละเอียด"
              value={productDetails.description ?? "-"}
            />
            <Details
              label="หมวดสินค้า"
              value={
                categoryName ? (
                  <Chip label={categoryName} size="small" variant="outlined" />
                ) : (
                  <GrayText>-</GrayText>
                )
              }
            />
            <Details
              label="ลำดับสินค้า"
              value={productDetails.sequence ?? EMPTY_PLACEHOLDER}
            />
            <Details
              label="ผู้ผลิต/แบรนด์"
              value={productDetails.brand ?? EMPTY_PLACEHOLDER}
            />
            <Details label="หน่วยนับ" value={unit} />
            <Details label="อายุการเก็บรักษา" value={shelfLife} />
            <Details label="ยอดของเสีย" value={wasteCount} />
          </Grid>
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <BlockTitle>ราคา</BlockTitle>
          <Grid container>
            <Details label="ราคาหน้าซอง" value={facePrice} />
            <Details
              label="ภาษี"
              value={<TaxInfo taxRate={productDetails.tax_rate} />}
            />
          </Grid>

          <Box sx={{ mt: 2, [compact]: { mt: 1.25 } }}>
            <BlockTitle>ข้อมูลเพิ่มเติม</BlockTitle>
            <Grid container>
              <BarcodeDetail
                label="บาร์โค้ดชิ้น"
                code={productDetails.barcode1}
              />
              <BarcodeDetail
                label="บาร์โค้ดลัง"
                code={productDetails.barcode2}
              />
              <Details
                label="เลขอ้างอิง"
                value={productDetails.reference1 ?? EMPTY_PLACEHOLDER}
              />
              <Details
                label="ขนาด (ซม.)"
                value={productDetails.dimension_text ?? EMPTY_PLACEHOLDER}
              />
              <Details
                label="เลขที่ อย."
                value={productDetails.fda_no ?? EMPTY_PLACEHOLDER}
              />
              <Details
                label={WEB_IT_EXPORT_LABEL}
                value={<ExportInfo allowExport={productDetails.allow_export} />}
              />
              <Details
                label="แก้ไขโดย"
                value={productDetails.updated_by_name ?? EMPTY_PLACEHOLDER}
              />
              <Details
                label="แก้ไขเมื่อ"
                value={
                  formatDateTimeBkk(productDetails.updated_at) ||
                  EMPTY_PLACEHOLDER
                }
              />
            </Grid>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, lg: 5 }}>
          <ImageInfo productDetails={productDetails} />
        </Grid>
      </Grid>
    </Paper>
  );
}
